import type { FullResult } from '@playwright/test/reporter';
import type { QuarantineList, RunQuarantineBlock } from '../types/index.js';

export type QuarantineUnavailableReason = 'unreachable' | 'timeout' | 'not_found';

export type QuarantineFetchOutcome = { list: QuarantineList } | { reason: QuarantineUnavailableReason };

export interface FinishedTest {
  id: string;
  title: string;
  outcome: 'expected' | 'unexpected' | 'flaky' | 'skipped';
  /** Message of the final attempt's first error; empty when it has none. */
  errorMessage: string;
}

export interface TestControlsInput {
  fetch: QuarantineFetchOutcome;
  tests: FinishedTest[];
  runStatus: FullResult['status'];
  globalErrorCount: number;
}

export interface TestControlsDecision {
  block: RunQuarantineBlock;
  /** Status to return from onEnd, or undefined to leave Playwright's. */
  status?: 'passed' | 'failed';
  /** Console lines, printed in order at the end of the run. */
  report: ReportLine[];
}

export interface ReportLine {
  tone: 'info' | 'success' | 'error' | 'warn';
  text: string;
}

// eslint-disable-next-line no-control-regex -- stripping ANSI is the point
const ANSI = /\u001b\[[0-9;]*m/g;

/**
 * The line a known-error pattern is tested against: the first non-empty line,
 * without ANSI colour, whitespace collapsed. The server builds patterns for
 * exactly this shape (root docs/QUARANTINE.md).
 */
export function firstErrorLine(message: string): string {
  for (const raw of message.replace(ANSI, '').split('\n')) {
    const line = raw.replace(/\s+/g, ' ').trim();
    if (line) return line;
  }
  return '';
}

interface KnownError {
  testIds: Set<string>;
  pattern: RegExp;
}

// A pattern that does not compile forgives nothing.
function compileKnownErrors(list: QuarantineList): KnownError[] {
  const out: KnownError[] = [];
  for (const entry of list.knownErrors ?? []) {
    try {
      out.push({ testIds: new Set(entry.testIds), pattern: new RegExp(entry.pattern) });
    } catch {
      continue;
    }
  }
  return out;
}

const UNAVAILABLE_TEXT: Record<QuarantineUnavailableReason, string> = {
  unreachable: "couldn't reach TestDino",
  timeout: 'TestDino did not answer in time',
  not_found: 'the project was not found',
};

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

function rulesLine(list: QuarantineList): ReportLine {
  const counts = list.rules;
  if (!counts) return { tone: 'info', text: 'TestDino Test Controls: rules loaded' };
  const parts = [
    counts.quarantined > 0 && `${counts.quarantined} quarantined`,
    counts.knownErrors > 0 && plural(counts.knownErrors, 'known error', 'known errors'),
    counts.critical > 0 && `${counts.critical} critical`,
  ].filter(Boolean);
  const total = counts.quarantined + counts.knownErrors + counts.critical;
  const detail = parts.length > 0 ? ` (${parts.join(', ')})` : '';
  return { tone: 'info', text: `TestDino Test Controls: ${plural(total, 'rule', 'rules')}${detail}` };
}

type Decided = Omit<TestControlsDecision, 'block'> & { block: Omit<RunQuarantineBlock, 'build'> };

/** Decides the build outcome under Test Controls and records it in the block. */
export function decideTestControls(input: TestControlsInput): TestControlsDecision {
  const decision = decide(input);
  const build = decision.status ?? (input.runStatus === 'passed' ? 'passed' : 'failed');
  return { ...decision, block: { ...decision.block, build } };
}

/**
 * Decides the build outcome under Test Controls. Quarantined and known-error
 * failures stop failing the build only when every unexpected test is forgiven,
 * nothing failed outside a test, and Playwright reported a plain failure. A
 * critical test that passed only on retry fails the build. Without the list,
 * nothing changes.
 */
function decide(input: TestControlsInput): Decided {
  if (!('list' in input.fetch)) {
    const reason = input.fetch.reason;
    return {
      block: { state: 'not_applied', reason },
      report: [
        {
          tone: 'warn',
          text: `TestDino Test Controls: rules not loaded (${UNAVAILABLE_TEXT[reason]}); failures count as usual`,
        },
      ],
    };
  }

  const list = input.fetch.list;
  const quarantined = new Set(list.quarantined ?? []);
  const critical = new Set(list.critical ?? []);
  const knownErrors = compileKnownErrors(list);
  const matchesKnownError = (test: FinishedTest): boolean => {
    const line = firstErrorLine(test.errorMessage);
    return knownErrors.some((k) => k.testIds.has(test.id) && k.pattern.test(line));
  };
  const isKnownErrorTest = (id: string): boolean => knownErrors.some((k) => k.testIds.has(id));

  const testIds: string[] = [];
  const forgiven = new Set<string>();
  const criticalFlaky: FinishedTest[] = [];
  const unexpected: FinishedTest[] = [];

  for (const test of input.tests) {
    if (test.outcome === 'skipped') continue;
    if (test.outcome === 'unexpected') unexpected.push(test);
    if (test.outcome === 'flaky' && critical.has(test.id)) criticalFlaky.push(test);

    if (quarantined.has(test.id)) {
      testIds.push(test.id);
      if (test.outcome === 'unexpected') forgiven.add(test.id);
    } else if (isKnownErrorTest(test.id)) {
      if (test.outcome !== 'unexpected') {
        testIds.push(test.id);
      } else if (matchesKnownError(test)) {
        testIds.push(test.id);
        forgiven.add(test.id);
      }
    }
  }

  const block: Decided['block'] = {
    state: 'applied',
    ...(testIds.length > 0 && { testIds }),
    ...(criticalFlaky.length > 0 && { criticalFlakyTestIds: criticalFlaky.map((t) => t.id) }),
  };

  // Nothing quarantined ran and no critical test flaked: the rules line alone
  // shows Test Controls was active.
  const report: ReportLine[] = [rulesLine(list)];
  if (testIds.length === 0 && criticalFlaky.length === 0) return { block, report };

  const excluded = forgiven.size;
  if (testIds.length > 0) {
    const failed = excluded > 0 ? `${excluded} failed, excluded from the build` : 'none failed';
    report.push({ tone: 'info', text: `Quarantined tests in this run: ${testIds.length} (${failed})` });
  }

  const isPlainEnd = input.runStatus === 'passed' || input.runStatus === 'failed';
  if (isPlainEnd && criticalFlaky.length > 0) {
    const names = criticalFlaky.map((t) => t.title).join(', ');
    const what = criticalFlaky.length === 1 ? 'critical test' : 'critical tests';
    report.push({ tone: 'error', text: `Build fails: ${what} passed only on retry (${names})` });
    return { block, status: 'failed', report };
  }

  const stillFailing = unexpected.filter((t) => !forgiven.has(t.id)).length;
  if (!isPlainEnd) {
    report.push({ tone: 'warn', text: `The run ended ${input.runStatus}, so its status stands` });
    return { block, report };
  }
  if (stillFailing > 0) {
    report.push({ tone: 'error', text: `Build fails: ${plural(stillFailing, 'failure', 'failures')} not quarantined` });
    return { block, report };
  }
  if (input.globalErrorCount > 0) {
    report.push({ tone: 'error', text: 'Build fails: an error outside a test' });
    return { block, report };
  }
  if (excluded > 0) {
    report.push({ tone: 'success', text: 'Build passes: every failure was quarantined' });
    return { block, status: input.runStatus === 'failed' ? 'passed' : undefined, report };
  }
  report.push({ tone: 'success', text: 'No failures' });
  return { block, report };
}
